import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Link } from '@/lib/router-components';
import { useAuthStore } from '../stores/auth.store';

export const Route = createFileRoute('/prometeus')({
  component: PrometeusLandingPage,
});

function PrometeusLandingPage() {
  const user = useAuthStore((state) => state.user);
  const navigate = useNavigate();

  const handleGoToPanel = () => {
    if (user) {
      navigate({ to: '/workspaces' });
    } else {
      navigate({ to: '/login' });
    }
  };

  return (
    <div
      className="min-h-screen bg-[#CCD0CF] text-[#06141B]"
      style={{ fontFamily: 'Poppins, sans-serif' }}
    >
      {/* Top bar */}
      <header className="sticky top-0 z-20 bg-[#CCD0CF]/90 backdrop-blur border-b border-[#9BA8AB]/40">
        <div className="mx-auto max-w-6xl px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/logo-landing.svg"
              alt="Prometeus"
              className="h-8 w-auto"
            />
            <span className="hidden sm:inline text-xs font-medium text-[#253745]">
              Prometeus – Operação jurídica em um só lugar
            </span>
          </div>
          <button
            type="button"
            onClick={handleGoToPanel}
            className="inline-flex items-center justify-center px-4 py-2 rounded-full text-xs sm:text-sm font-medium text-white bg-[#06141B] hover:bg-[#11212D] transition-colors shadow-md"
          >
            Ir para o painel
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-6xl px-6 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center rounded-full bg-[#11212D] text-[#CCD0CF] px-3 py-1 text-xs font-medium mb-4">
              Plataforma para escritórios jurídicos que querem crescer com previsibilidade
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight text-[#06141B] mb-4">
              Simplifique a gestão
              <span className="block text-[#253745]">
                do seu escritório com o Prometeus
              </span>
            </h1>
            <p className="text-sm sm:text-base text-[#253745] mb-6 max-w-xl">
              Centralize processos, clientes, tarefas e cobranças em um único painel.
              Tenha visão clara da operação, reduza trabalho manual e ganhe tempo para
              o que realmente importa: advogar.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <Link
                to="/login"
                search={{ tab: 'register' } as any}
                className="inline-flex items-center justify-center px-6 py-3 rounded-full text-sm font-medium text-white bg-gradient-to-r from-[#06141B] via-[#11212D] to-[#253745] shadow-lg shadow-[#06141B]/40 hover:shadow-xl hover:shadow-[#06141B]/50 transition-all"
              >
                Criar minha conta grátis
              </Link>
              <a
                href={import.meta.env.VITE_WHATSAPP_URL || 'https://wa.me/5591989045502'}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-6 py-3 rounded-full text-sm font-medium border border-[#4A5C6A]/40 text-[#06141B] bg-white/70 hover:bg-white transition-colors"
              >
                Falar com especialista
              </a>
            </div>

            <div className="flex flex-wrap gap-4 text-xs text-[#4A5C6A]">
              <div className="flex items-center gap-2">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#06141B] text-[#CCD0CF] text-[10px] font-semibold">
                  5k+
                </span>
                <span>cobranças gerenciadas pelo Prometeus</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#253745] text-[#CCD0CF] text-[10px] font-semibold">
                  0
                </span>
                <span>mensalidade fixa, você paga por uso</span>
              </div>
            </div>
          </div>

          {/* Mockup / painel */}
          <div className="relative">
            <img
              src="/logo-landing.svg"
              alt="Prometeus"
              className="absolute -top-6 -right-4 h-10 w-auto opacity-80"
            />
            <div className="absolute inset-0 -right-10 rounded-[40px] bg-gradient-to-br from-[#06141B] via-[#11212D] to-[#253745] blur-3xl opacity-70" />
            <div className="relative grid gap-4">
              <div className="rounded-3xl bg-[#06141B] text-[#CCD0CF] p-5 shadow-2xl border border-[#4A5C6A]/40">
                <div className="flex items-center justify-between mb-3 text-xs text-[#9BA8AB]">
                  <span>Dashboard financeiro</span>
                  <span>Visão geral do mês</span>
                </div>
                <h2 className="text-lg font-semibold mb-1">R$ 180.275,00</h2>
                <p className="text-[11px] text-[#9BA8AB] mb-4">Recebidos no período</p>
                <div className="grid grid-cols-3 gap-3 text-[11px]">
                  <div className="rounded-xl bg-[#11212D] p-3">
                    <p className="text-[#9BA8AB] mb-1">Clientes ativos</p>
                    <p className="text-base font-semibold">97</p>
                  </div>
                  <div className="rounded-xl bg-[#11212D] p-3">
                    <p className="text-[#9BA8AB] mb-1">Processos em dia</p>
                    <p className="text-base font-semibold">241</p>
                  </div>
                  <div className="rounded-xl bg-[#11212D] p-3">
                    <p className="text-[#9BA8AB] mb-1">Tarefas concluídas</p>
                    <p className="text-base font-semibold">83%</p>
                  </div>
                </div>
              </div>

              <div className="rounded-3xl bg-[#CCD0CF] text-[#06141B] p-5 shadow-xl border border-[#9BA8AB]/60">
                <p className="text-xs font-medium text-[#11212D] mb-3">
                  Tudo em um só painel
                </p>
                <ul className="space-y-2 text-xs text-[#253745]">
                  <li className="flex items-center gap-2">
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#06141B] text-[#CCD0CF] text-[9px]">
                      ✓
                    </span>
                    Gestão de processos, clientes e atividades
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#253745] text-[#CCD0CF] text-[9px]">
                      ✓
                    </span>
                    Cobranças automatizadas via Pix, boleto e cartão
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#4A5C6A] text-[#CCD0CF] text-[9px]">
                      ✓
                    </span>
                    Alertas inteligentes para não perder prazos
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dores */}
      <section className="bg-[#F4F5F5] py-16">
        <div className="mx-auto max-w-6xl px-6 grid lg:grid-cols-[1fr,1.2fr] gap-12 items-center">
          <div className="space-y-4">
            {[
              'Tempo perdido com tarefas operacionais.',
              'Informações espalhadas em planilhas e grupos.',
              'Dificuldade para enxergar o que realmente está em risco.',
            ].map((text) => (
              <div
                key={text}
                className="rounded-2xl bg-[#9BA8AB] text-[#06141B] px-5 py-4 flex items-center justify-between shadow-sm"
              >
                <span className="text-sm font-medium">{text}</span>
                <span className="ml-4 inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#06141B]/20 text-xs">
                  •••
                </span>
              </div>
            ))}
          </div>

          <div>
            <p className="text-xs font-semibold tracking-[0.16em] uppercase text-[#4A5C6A] mb-3">
              A dor que trava o seu crescimento
            </p>
            <h2 className="text-2xl sm:text-3xl font-semibold text-[#06141B] mb-4">
              Seu escritório cresceu, mas sua operação ainda não acompanhou.
            </h2>
            <p className="text-sm text-[#253745] mb-3">
              E-mails, mensagens, planilhas e tarefas avulsas parecem dar conta do
              recado por um tempo. Até que os prazos começam a apertar, cobranças
              se perdem e a equipe trabalha no modo apagador de incêndio.
            </p>
            <p className="text-sm text-[#06141B] font-medium">
              O Prometeus organiza o caos da rotina jurídica para você focar em
              estratégia e resultado.
            </p>
          </div>
        </div>
      </section>

      {/* Solução */}
      <section className="py-16">
        <div className="mx-auto max-w-6xl px-6 space-y-10">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-xs font-semibold tracking-[0.16em] uppercase text-[#4A5C6A] mb-3">
              A solução está aqui: Prometeus
            </p>
            <h2 className="text-2xl sm:text-3xl font-semibold text-[#06141B] mb-3">
              Automatize a jornada do seu escritório em uma única plataforma.
            </h2>
            <p className="text-sm text-[#253745]">
              Do primeiro contato com o cliente ao acompanhamento do processo e
              faturamento, o Prometeus conecta todas as etapas em um fluxo simples,
              visual e seguro.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: 'Operação em um só painel',
                description:
                  'Visualize equipe, tarefas, prazos e processos em tempo real, sem abrir dezenas de abas.',
              },
              {
                title: 'Cobrança recorrente integrada',
                description:
                  'Automatize boletos, Pix e cartão, reduza inadimplência e pare de fazer cobrança manual.',
              },
              {
                title: 'Gestão orientada a dados',
                description:
                  'Acompanhe indicadores de produtividade, faturamento e riscos com poucos cliques.',
              },
            ].map((item) => (
              <div
                key={item.title}
                className="rounded-3xl bg-white/80 border border-[#9BA8AB]/40 p-6 shadow-sm flex flex-col"
              >
                <div className="mb-4 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#06141B] text-[#CCD0CF] text-xs font-semibold">
                  {item.title.charAt(0)}
                </div>
                <h3 className="text-base font-semibold text-[#06141B] mb-2">
                  {item.title}
                </h3>
                <p className="text-xs text-[#253745] flex-1">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Seção \"Tudo em um só app\" */}
      <section className="bg-[#253745] text-[#CCD0CF] py-16">
        <div className="mx-auto max-w-6xl px-6 grid lg:grid-cols-[1.1fr,1fr] gap-10 items-center">
          <div>
            <h2 className="text-2xl sm:text-3xl font-semibold mb-3">
              Tudo em um só app:
            </h2>
            <p className="text-sm text-[#CCD0CF] mb-6 max-w-md">
              Crie tarefas, organize audiências, acompanhe processos, registre
              contatos e monitore cobranças em tempo real — do computador ou do
              celular.
            </p>
            <ul className="space-y-3 text-sm">
              {[
                'Visão consolidada por cliente, processo e área.',
                'Alertas sobre prazos críticos e tarefas atrasadas.',
                'Histórico completo das interações com o cliente.',
                'Equipe na mesma página, sem ruídos de comunicação.',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span className="mt-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#06141B] text-[10px]">
                    ✓
                  </span>
                  <span className="text-xs sm:text-sm">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="relative">
            <div className="absolute inset-0 -right-6 rounded-[32px] bg-gradient-to-tr from-[#06141B] via-[#11212D] to-[#4A5C6A] blur-2xl opacity-80" />
            <div className="relative rounded-[32px] bg-[#06141B] text-[#CCD0CF] p-6 shadow-2xl border border-[#4A5C6A]/60">
              <p className="text-xs text-[#9BA8AB] mb-2">Resumo do dia</p>
              <h3 className="text-lg font-semibold mb-4">
                24 atividades concluídas
              </h3>
              <div className="space-y-3 text-xs">
                <div className="flex items-center justify-between">
                  <span>Reuniões realizadas</span>
                  <span className="font-semibold">8</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Andamentos registrados</span>
                  <span className="font-semibold">15</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Novas oportunidades</span>
                  <span className="font-semibold">5</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sem planos fixos */}
      <section className="py-16">
        <div className="mx-auto max-w-6xl px-6 grid lg:grid-cols-[1.2fr,1fr] gap-10 items-center">
          <div className="rounded-[32px] bg-[#253745] text-[#CCD0CF] p-8 shadow-xl">
            <p className="text-sm sm:text-lg font-semibold mb-3">
              Sem planos fixos nem fidelidade.
            </p>
            <p className="text-sm text-[#CCD0CF] mb-5 max-w-md">
              O Prometeus foi pensado para acompanhar o crescimento do seu
              escritório — não para engessar seu caixa.
            </p>
            <ul className="space-y-2 text-sm">
              <li>- Pague por uso, de acordo com o volume do seu escritório.</li>
              <li>- Nada de mensalidade pesada no começo da operação.</li>
              <li>- Sem sustos no fim do mês: custos claros e previsíveis.</li>
            </ul>
          </div>

          <div className="space-y-4">
            <h3 className="text-base sm:text-lg font-semibold text-[#06141B]">
              Ideal para quem vive de recorrência:
            </h3>
            <div className="rounded-3xl bg-white/80 border border-[#9BA8AB]/50 p-5 space-y-2 text-xs text-[#253745]">
              {[
                'Escritórios boutique com carteira recorrente.',
                'Departamentos jurídicos que lidam com alto volume.',
                'Advogados autônomos em fase de estruturação.',
                'Times que querem profissionalizar a operação.',
              ].map((item) => (
                <div
                  key={item}
                  className="inline-flex items-center justify-between w-full rounded-full bg-[#CCD0CF] px-4 py-2"
                >
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Prova social */}
      <section className="bg-[#06141B] text-[#CCD0CF] py-16">
        <div className="mx-auto max-w-6xl px-6 space-y-8">
          <div className="max-w-3xl">
            <h2 className="text-2xl sm:text-3xl font-semibold mb-3">
              Mais tempo para advogar, menos tempo apagando incêndio.
            </h2>
            <p className="text-sm text-[#9BA8AB]">
              Mais de 5.000 cobranças e centenas de processos já são gerenciados no
              Prometeus para que escritórios automatizem rotinas e ganhem previsibilidade.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 text-xs">
            {[
              {
                label: 'Cobranças automatizadas',
                value: '5.000+',
              },
              {
                label: 'Redução média de trabalho operacional',
                value: '30%',
              },
              {
                label: 'Aumento médio de visibilidade sobre prazos',
                value: '4x',
              },
            ].map((item) => (
              <div
                key={item.label}
                className="rounded-3xl bg-[#11212D] border border-[#4A5C6A]/60 p-5"
              >
                <p className="text-[11px] text-[#9BA8AB] mb-2">{item.label}</p>
                <p className="text-xl font-semibold">{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="py-16">
        <div className="mx-auto max-w-4xl px-6 text-center rounded-[32px] bg-gradient-to-r from-[#06141B] via-[#11212D] to-[#253745] text-[#CCD0CF] py-12 shadow-2xl">
          <h2 className="text-2xl sm:text-3xl font-semibold mb-3">
            Chega de planilhas, retrabalho e falta de visibilidade.
          </h2>
          <p className="text-sm text-[#CCD0CF] mb-6 max-w-2xl mx-auto">
            Com o Prometeus, você automatiza a operação do seu escritório, reduz
            riscos e ganha tempo para focar na estratégia dos casos.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <Link
              to="/login"
              search={{ tab: 'register' } as any}
              className="inline-flex items-center justify-center px-6 py-3 rounded-full text-sm font-medium text-[#06141B] bg-[#CCD0CF] hover:bg-white transition-colors shadow-md"
            >
              Criar minha conta grátis
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center justify-center px-6 py-3 rounded-full text-sm font-medium border border-[#9BA8AB]/70 text-[#CCD0CF] hover:bg-white/5 transition-colors"
            >
              Já tenho conta, entrar
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ simples */}
      <section className="bg-[#F4F5F5] py-16">
        <div className="mx-auto max-w-4xl px-6">
          <h2 className="text-2xl font-semibold text-center text-[#06141B] mb-8">
            FAQ
          </h2>
          <div className="space-y-4">
            {[
              {
                q: 'O Prometeus tem período de teste?',
                a: 'Sim. Você pode criar sua conta e testar os principais recursos sem compromisso. Nosso time acompanha sua implantação para garantir uma boa experiência.',
              },
              {
                q: 'Preciso migrar tudo de uma vez?',
                a: 'Não. Muitos clientes começam por uma frente específica (como tarefas ou cobranças) e vão expandindo o uso conforme a equipe se adapta.',
              },
              {
                q: 'O Prometeus substitui meu financeiro?',
                a: 'Ele organiza e automatiza boa parte da rotina de cobranças e acompanhamento, mas pode ser integrado ao seu fluxo financeiro atual.',
              },
            ].map((item) => (
              <details
                key={item.q}
                className="group rounded-2xl bg-white border border-[#CCD0CF] px-5 py-4 text-sm"
              >
                <summary className="flex items-center justify-between cursor-pointer text-[#06141B] font-medium">
                  <span>{item.q}</span>
                  <span className="ml-4 inline-flex h-6 w-6 items-center justify-center rounded-full border border-[#9BA8AB] text-xs group-open:rotate-45 transition-transform">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-xs text-[#253745]">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Footer institucional */}
      <footer className="bg-[#06141B] text-[#CCD0CF] py-10">
        <div className="mx-auto max-w-6xl px-6 grid gap-8 md:grid-cols-[1.2fr,1fr,1fr]">
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <img
                src="/logo-landing.svg"
                alt="Prometeus"
                className="h-8 w-auto"
              />
              <span className="text-sm font-semibold">Prometeus</span>
            </div>
            <p className="text-xs text-[#9BA8AB] max-w-sm">
              Plataforma focada em organizar a operação jurídica de escritórios e
              profissionais autônomos, conectando processos, clientes, tarefas e
              cobranças em um só lugar.
            </p>
          </div>

          <div>
            <h3 className="text-sm font-semibold mb-3">Contato</h3>
            <ul className="space-y-1 text-xs text-[#CCD0CF]">
              <li>
                R. Petúnia, 32-138 - Jardim Industrial, Maringá - PR, 87065-300
              </li>
              <li>E-mail: contato@mourarezende.com.br</li>
              <li>Telefone: (91) 9 8904-5502</li>
              <li>Empresa: Prometeus</li>
              <li>Responsável: Gabryel Davi de Moura Rezende</li>
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-semibold mb-3">Legal</h3>
            <ul className="space-y-2 text-xs">
              <li>
                <Link
                  to="/termos"
                  className="hover:underline text-[#CCD0CF]"
                >
                  Termos e Condições de Uso
                </Link>
              </li>
              <li>
                <Link
                  to="/politica-privacidade-cookies"
                  className="hover:underline text-[#CCD0CF]"
                >
                  Política de Privacidade e Cookies
                </Link>
              </li>
              <li>
                <Link
                  to="/politica-cancelamento-reembolso"
                  className="hover:underline text-[#CCD0CF]"
                >
                  Política de Cancelamento e Reembolso
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div className="mt-8 border-t border-[#4A5C6A]/60 pt-4 text-center text-[11px] text-[#9BA8AB]">
          © {new Date().getFullYear()} Prometeus. Todos os direitos reservados.
        </div>
      </footer>
    </div>
  );
}

